/**
 * Ticket Detail Page
 * @description 티켓 상세 페이지 - 기본 정보, 승인 정보, 타임라인, 승인/거부 기능
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@shared/router';
import { Badge, Button, Card } from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import {
    ArrowLeft,
    Edit,
    CheckCircle,
    XCircle,
    Clock,
    User,
    Calendar,
    ThumbsUp,
    ThumbsDown,
} from 'lucide-react';
import { getTicket, approveTicket, rejectTicket, changeTicketStatus, assignTicket, closeTicket } from '@/services/ticketApi';
import { ApprovalModal } from '../components/ApprovalModal';
import { ChangeStatusModal } from '../components/ChangeStatusModal';
import { AssignTicketModal } from '../components/AssignTicketModal';
import { CloseTicketModal } from '../components/CloseTicketModal';
import type { Ticket, TicketStatus, Priority } from '@/types/ticket.types';

const statusLabels: Record<TicketStatus, string> = {
    draft: '임시저장',
    submitted: '제출됨',
    in_progress: '진행중',
    pending: '대기중',
    resolved: '해결됨',
    closed: '완료',
    rejected: '거부됨',
    cancelled: '취소됨',
};

const priorityLabels: Record<Priority, string> = {
    low: '낮음',
    medium: '보통',
    high: '높음',
    critical: '긴급',
};

const priorityColors: Record<Priority, string> = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-blue-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
};

export const TicketDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams({ from: '/it-service/tickets/$ticketId' });
    const user = useAuthStore((state) => state.user);

    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 승인/거부 모달 상태
    const [approvalModalOpen, setApprovalModalOpen] = useState(false);
    const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');

    // 상태 변경 모달
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    // 담당자 할당 모달
    const [assignModalOpen, setAssignModalOpen] = useState(false);

    // 티켓 닫기 모달
    const [closeModalOpen, setCloseModalOpen] = useState(false);

    useEffect(() => {
        loadTicket();
    }, [params.ticketId]);

    const loadTicket = async () => {
        if (!user || user.userType !== 'tenant-user') {
            setError('Tenant 사용자만 접근 가능합니다.');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const data = await getTicket(user.tenantId, params.ticketId);
            setTicket(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : '티켓을 불러올 수 없습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        navigate({ to: '/it-service/servicerequests' });
    };

    const handleEdit = () => {
        navigate({ to: `/it-service/tickets/${params.ticketId}/edit` });
    };

    const handleApprove = () => {
        setApprovalAction('approve');
        setApprovalModalOpen(true);
    };

    const handleReject = () => {
        setApprovalAction('reject');
        setApprovalModalOpen(true);
    };

    const handleApprovalConfirm = async (comment?: string) => {
        if (!user || user.userType !== 'tenant-user') return;

        try {
            if (approvalAction === 'approve') {
                await approveTicket(user.tenantId, params.ticketId, { comment });
            } else {
                await rejectTicket(user.tenantId, params.ticketId, { comment });
            }
            await loadTicket();
        } catch (err) {
            setError(err instanceof Error ? err.message : '승인/거부 처리에 실패했습니다.');
        }
    };

    const handleStatusChange = async (status: TicketStatus, comment?: string) => {
        if (!user || user.userType !== 'tenant-user') return;

        try {
            await changeTicketStatus(user.tenantId, params.ticketId, { status, comment });
            await loadTicket();
        } catch (err) {
            setError(err instanceof Error ? err.message : '상태 변경에 실패했습니다.');
        }
    };

    const handleAssign = async (assignee: string, comment?: string) => {
        if (!user || user.userType !== 'tenant-user') return;

        try {
            await assignTicket(user.tenantId, params.ticketId, { assignee, comment });
            await loadTicket();
        } catch (err) {
            setError(err instanceof Error ? err.message : '담당자 할당에 실패했습니다.');
        }
    };

    const handleClose = async (resolution: string) => {
        if (!user || user.userType !== 'tenant-user') return;

        try {
            await closeTicket(user.tenantId, params.ticketId, { resolution });
            await loadTicket();
        } catch (err) {
            setError(err instanceof Error ? err.message : '티켓 닫기에 실패했습니다.');
        }
    };

    // 현재 사용자가 승인자인지 확인
    const canApprove =
        ticket?.approval &&
        user?.userType === 'tenant-user' &&
        ticket.approval.status === 'pending' &&
        ticket.approval.steps[ticket.approval.currentStep - 1]?.approvers.some(
            (approver) => approver.userId === user.userId
        );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">로딩 중...</p>
            </div>
        );
    }

    if (error || !ticket) {
        return (
            <div className="space-y-4">
                <Button variant="outline" onClick={handleBack} className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    목록으로
                </Button>
                <Card className="p-8 text-center">
                    <p className="text-danger">{error || '티켓을 찾을 수 없습니다.'}</p>
                </Card>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-8">
                {/* Enhanced Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-xl p-6 border border-blue-100 dark:border-blue-800/30">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-6">
                            <Button
                                variant="outline"
                                onClick={handleBack}
                                className="gap-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800 shadow-sm"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                목록으로
                            </Button>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-700 dark:text-gray-300">
                                        {ticket.ticketId}
                                    </span>
                                    <Badge className={`${priorityColors[ticket.priority]} font-medium px-3 py-1`}>
                                        {priorityLabels[ticket.priority]}
                                    </Badge>
                                    <Badge
                                        variant="outline"
                                        className={`font-medium px-3 py-1 ${ticket.status === 'closed' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800' :
                                            ticket.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-800' :
                                                ticket.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-800' :
                                                    'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950/20 dark:text-gray-400 dark:border-gray-800'
                                            }`}
                                    >
                                        {statusLabels[ticket.status]}
                                    </Badge>
                                </div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white leading-tight">
                                    {ticket.title}
                                </h1>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {canApprove && (
                                <>
                                    <Button
                                        onClick={handleApprove}
                                        className="gap-2 bg-green-600 hover:bg-green-700 text-white shadow-sm"
                                    >
                                        <ThumbsUp className="w-4 h-4" />
                                        승인
                                    </Button>
                                    <Button
                                        onClick={handleReject}
                                        variant="destructive"
                                        className="gap-2 shadow-sm"
                                    >
                                        <ThumbsDown className="w-4 h-4" />
                                        거부
                                    </Button>
                                </>
                            )}
                            <Button
                                onClick={() => setStatusModalOpen(true)}
                                variant="outline"
                                className="gap-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800 shadow-sm"
                            >
                                <Clock className="w-4 h-4" />
                                상태 변경
                            </Button>
                            <Button
                                onClick={() => setAssignModalOpen(true)}
                                variant="outline"
                                className="gap-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800 shadow-sm"
                            >
                                <User className="w-4 h-4" />
                                담당자 할당
                            </Button>
                            {ticket.status !== 'closed' && (
                                <Button
                                    onClick={() => setCloseModalOpen(true)}
                                    variant="outline"
                                    className="gap-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800 shadow-sm"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    티켓 닫기
                                </Button>
                            )}
                            <Button
                                onClick={handleEdit}
                                variant="outline"
                                className="gap-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800 shadow-sm"
                            >
                                <Edit className="w-4 h-4" />
                                수정
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Enhanced Description Card */}
                        <Card className="p-8 shadow-sm border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-2 h-8 bg-blue-500 rounded-full"></div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">설명</h2>
                            </div>
                            <div className="prose prose-gray dark:prose-invert max-w-none">
                                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                                    {ticket.description}
                                </p>
                            </div>
                        </Card>

                        {/* Enhanced Custom Fields */}
                        {ticket.customFields && Object.keys(ticket.customFields).length > 0 && (
                            <Card className="p-8 shadow-sm border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-2 h-8 bg-purple-500 rounded-full"></div>
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">추가 정보</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {Object.entries(ticket.customFields).map(([key, value]) => (
                                        <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                                {key}
                                            </span>
                                            <span className="text-gray-900 dark:text-white font-medium">
                                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Enhanced Approval Info */}
                        {ticket.approval && (
                            <Card className="p-8 shadow-sm border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-2 h-8 bg-green-500 rounded-full"></div>
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">승인 정보</h2>
                                </div>
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">승인 상태:</span>
                                            <Badge
                                                variant="outline"
                                                className={`font-medium ${ticket.approval.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800' :
                                                    ticket.approval.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-800' :
                                                        'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-800'
                                                    }`}
                                            >
                                                {ticket.approval.status}
                                            </Badge>
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                            Step {ticket.approval.currentStep} / {ticket.approval.steps.length}
                                        </div>
                                    </div>

                                    {/* Enhanced Approval Steps */}
                                    <div className="space-y-4">
                                        {ticket.approval.steps.map((step) => (
                                            <div
                                                key={step.step}
                                                className={`p-6 rounded-xl border-2 transition-all ${step.step === ticket.approval!.currentStep
                                                    ? 'border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/20'
                                                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step.step === ticket.approval!.currentStep
                                                            ? 'bg-blue-500 text-white'
                                                            : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                                                            }`}>
                                                            {step.step}
                                                        </div>
                                                        <span className="font-semibold text-gray-900 dark:text-white">
                                                            Step {step.step}
                                                        </span>
                                                    </div>
                                                    <Badge
                                                        variant="outline"
                                                        className={`${step.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800' :
                                                            step.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-800' :
                                                                'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950/20 dark:text-gray-400 dark:border-gray-800'
                                                            }`}
                                                    >
                                                        {step.status}
                                                    </Badge>
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    승인자: {step.approvers.map((a) => a.displayName || a.userId).join(', ')}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Enhanced Approval History */}
                                    {ticket.approval.history.length > 0 && (
                                        <div className="mt-8">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">승인 이력</h3>
                                            <div className="space-y-4">
                                                {ticket.approval.history.map((history, index) => (
                                                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-l-4 border-l-blue-500">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            {history.action === 'approve' ? (
                                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                                            ) : (
                                                                <XCircle className="w-5 h-5 text-red-500" />
                                                            )}
                                                            <span className="font-semibold text-gray-900 dark:text-white">
                                                                {history.userName || history.userId}
                                                            </span>
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-xs ${history.action === 'approve'
                                                                    ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800'
                                                                    : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-800'
                                                                    }`}
                                                            >
                                                                {history.action === 'approve' ? '승인' : '거부'}
                                                            </Badge>
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                {new Date(history.timestamp).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        {history.comment && (
                                                            <p className="text-gray-700 dark:text-gray-300 ml-8 italic">
                                                                "{history.comment}"
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}
                    </div>

                    {/* Enhanced Sidebar */}
                    <div className="space-y-8">
                        {/* Enhanced Basic Info */}
                        <Card className="p-8 shadow-sm border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-2 h-8 bg-indigo-500 rounded-full"></div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">기본 정보</h2>
                            </div>
                            <div className="space-y-6">
                                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">보고자</p>
                                        <p className="text-gray-900 dark:text-white font-semibold">
                                            {typeof ticket.reporter === 'object' ? ticket.reporter?.displayName || ticket.reporter?.userId : (ticket.reporterName || ticket.reporter)}
                                        </p>
                                    </div>
                                </div>

                                {(ticket.assignee || ticket.assigneeName) && (
                                    <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                            <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">담당자</p>
                                            <p className="text-gray-900 dark:text-white font-semibold">
                                                {typeof ticket.assignee === 'object' ? ticket.assignee?.displayName || ticket.assignee?.userId : (ticket.assigneeName || ticket.assignee)}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">생성일</p>
                                        <p className="text-gray-900 dark:text-white font-semibold">
                                            {new Date(ticket.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                                        <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">최종 수정</p>
                                        <p className="text-gray-900 dark:text-white font-semibold">
                                            {new Date(ticket.updatedAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>

                                {ticket.closedAt && (
                                    <div className="flex items-start gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">완료일</p>
                                            <p className="text-green-900 dark:text-green-300 font-semibold">
                                                {new Date(ticket.closedAt).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Enhanced External References */}
                        {ticket.externalRefs && ticket.externalRefs.length > 0 && (
                            <Card className="p-8 shadow-sm border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-2 h-8 bg-cyan-500 rounded-full"></div>
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">외부 시스템 연동</h2>
                                </div>
                                <div className="space-y-4">
                                    {ticket.externalRefs.map((ref, index) => (
                                        <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{ref.system}</p>
                                            <p className="font-mono text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 px-3 py-2 rounded border">
                                                {ref.refId}
                                            </p>
                                            {ref.url && (
                                                <a
                                                    href={ref.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 mt-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
                                                >
                                                    링크 열기
                                                    <ArrowLeft className="w-4 h-4 rotate-180" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            {/* Approval Modal */}
            <ApprovalModal
                isOpen={approvalModalOpen}
                onClose={() => setApprovalModalOpen(false)}
                onConfirm={handleApprovalConfirm}
                action={approvalAction}
                ticketId={ticket.ticketId}
            />

            {/* Status Change Modal */}
            <ChangeStatusModal
                isOpen={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                onConfirm={handleStatusChange}
                currentStatus={ticket.status}
            />

            {/* Assign Ticket Modal */}
            <AssignTicketModal
                isOpen={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                onConfirm={handleAssign}
                currentAssignee={ticket.assignee}
            />

            {/* Close Ticket Modal */}
            <CloseTicketModal
                isOpen={closeModalOpen}
                onClose={() => setCloseModalOpen(false)}
                onConfirm={handleClose}
                ticketId={ticket.ticketId}
            />
        </>
    );
};

TicketDetailPage.displayName = 'TicketDetailPage';
